import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { isAuthenticated, logout } from '../utils/auth';
import PageHeader from './PageHeader';
import './Profile.css';

const fetchOptions = { credentials: 'include', headers: { 'Content-Type': 'application/json' } };

const Profile = () => {
  const [user, setUser] = useState(null);
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleUnauthorized = useCallback(() => {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    logout();
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const [userRes, picksRes] = await Promise.all([
          fetch('/api/auth/me', fetchOptions),
          fetch('/api/picks', fetchOptions),
        ]);

        if (userRes.status === 401 || picksRes.status === 401) {
          handleUnauthorized();
          return;
        }

        const userData = await userRes.json();
        const picksData = await picksRes.json();

        setUser(userData);
        setPicks(picksData);
      } catch (error) {
        console.error('Error fetching profile data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, handleUnauthorized]);

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    logout();
    navigate('/login');
  };

  if (loading) {
    return <div className="profile"><p>Loading profile...</p></div>;
  }

  const completedPicks = picks.filter(p => p.raceId?.status === 'completed');

  const seasonPoints = user
    ? [...(user.seasonPoints || [])].sort((a, b) => b.year - a.year)
    : [];

  const currentYear = new Date().getFullYear();
  const currentYearPoints = (user?.seasonPoints || []).find(s => s.year === currentYear)?.points ?? 0;

  return (
    <div className="profile">
      <div className="profile-header">
        <PageHeader overline="Account & Stats" title="MY PROFILE" />
        <div className="profile-header-actions">
          <button onClick={() => navigate('/dashboard')} className="nav-button">
            ← Back to Dashboard
          </button>
          <button onClick={handleLogout} className="nav-button">
            Logout
          </button>
        </div>
      </div>

      {user && (
        <div className="profile-card">
          {user.avatar && (
            <img src={user.avatar} alt={user.name} className="profile-avatar" referrerPolicy="no-referrer" />
          )}
          <div className="profile-info">
            <h2 className="profile-name">{user.name}</h2>
            <p className="profile-email">{user.email}</p>
          </div>
          <div className="profile-points-section">
            <div className="profile-points-badge">
              <span className="profile-points-value">{currentYearPoints}</span>
              <span className="profile-points-label">{currentYear} Total Points</span>
            </div>
            {seasonPoints.length > 0 && (
              <div className="profile-season-breakdown">
                {seasonPoints.map(s => (
                  <div key={s.year} className="profile-season-pill">
                    <span className="profile-season-year">{s.year}</span>
                    <span className="profile-season-pts">{s.points ?? 0} pts</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="profile-history">
        <h2>📊 Pick History</h2>
        {completedPicks.length === 0 ? (
          <p className="no-picks">No completed race picks yet.</p>
        ) : (
          <div className="profile-picks-list">
            {completedPicks
              .slice()
              .sort((a, b) => new Date(b.raceId?.startTime || 0) - new Date(a.raceId?.startTime || 0))
              .map(pick => {
                const race = pick.raceId || {};
                return (
                  <div key={pick._id} className="profile-pick-row">
                    <div className="profile-pick-race">
                      {race._id
                        ? <Link to={`/races/${race._id}`} className="profile-race-link">{race.name || 'Unknown Race'}</Link>
                        : <span>{race.name || 'Unknown Race'}</span>
                      }
                      <div className="profile-pick-meta">
                        {race.startTime && (
                          <span className="profile-pick-date">
                            {new Date(race.startTime).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                          </span>
                        )}
                        {race.year && (
                          <span className="profile-pick-season">{race.year} Season</span>
                        )}
                      </div>
                    </div>
                    <div className="profile-pick-points">
                      <span className="profile-pick-points-value">{pick.points ?? 0}</span>
                      <span className="profile-pick-points-label">pts</span>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
